import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ProductCard from "../components/ProductCard";
import { fetchFilters, fetchProducts } from "../api/shop";

const SORTS = [
  { value: "", label: "Relevancia" },
  { value: "price_asc", label: "Precio: de menor a mayor" },
  { value: "price_desc", label: "Precio: de mayor a menor" },
  { value: "name", label: "Nombre A–Z" },
];

export default function Products() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState({ items: [], total: 0, pages: 1, page: 1 });
  const [filters, setFilters] = useState({ brands: [], minPrice: 0, maxPrice: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const q = params.get("q") || "";
  const brand = params.get("brand") || "";
  const sort = params.get("sort") || "";
  const minPrice = params.get("minPrice") || "";
  const maxPrice = params.get("maxPrice") || "";
  const page = Number(params.get("page")) || 1;

  // Draft price inputs, committed on submit so we don't refetch per keystroke.
  const [draft, setDraft] = useState({ min: minPrice, max: maxPrice });
  useEffect(() => setDraft({ min: minPrice, max: maxPrice }), [minPrice, maxPrice]);

  useEffect(() => {
    fetchFilters().then(setFilters).catch(() => {});
  }, []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError("");

    fetchProducts({ q, brand, sort, minPrice, maxPrice, page, limit: 24 })
      .then((res) => alive && setData(res))
      .catch((err) => alive && setError(err.message))
      .finally(() => alive && setLoading(false));

    window.scrollTo({ top: 0, behavior: "smooth" });
    return () => {
      alive = false;
    };
  }, [q, brand, sort, minPrice, maxPrice, page]);

  /** Change one filter, reset to page 1, drop empty values from the URL. */
  const update = (patch) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([key, value]) => {
      if (value) next.set(key, value);
      else next.delete(key);
    });
    if (!("page" in patch)) next.delete("page");
    setParams(next);
  };

  const activeChips = [
    q && { key: "q", label: `“${q}”` },
    brand && { key: "brand", label: brand },
    minPrice && { key: "minPrice", label: `desde ${minPrice} €` },
    maxPrice && { key: "maxPrice", label: `hasta ${maxPrice} €` },
  ].filter(Boolean);

  return (
    <div className="wrap listing">
      {/* Filters */}
      <aside>
        <div className="filters">
          <h3>Marca</h3>
          <ul className="filterlist">
            <li>
              <button aria-pressed={!brand} onClick={() => update({ brand: "" })}>
                Todas <b>{filters.total || data.total}</b>
              </button>
            </li>
            {filters.brands.map(({ brand: name, count }) => (
              <li key={name}>
                <button aria-pressed={brand === name} onClick={() => update({ brand: name })}>
                  {name} <b>{count}</b>
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div className="filters">
          <h3>Precio</h3>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              update({ minPrice: draft.min, maxPrice: draft.max });
            }}
          >
            <div className="pricerange">
              <input
                type="number"
                min="0"
                inputMode="numeric"
                value={draft.min}
                onChange={(e) => setDraft({ ...draft, min: e.target.value })}
                placeholder={String(filters.minPrice || 0)}
                aria-label="Precio mínimo"
              />
              <span aria-hidden="true">—</span>
              <input
                type="number"
                min="0"
                inputMode="numeric"
                value={draft.max}
                onChange={(e) => setDraft({ ...draft, max: e.target.value })}
                placeholder={String(filters.maxPrice || 0)}
                aria-label="Precio máximo"
              />
            </div>
            <button type="submit" className="btn btn--ghost btn--block" style={{ marginTop: 12 }}>
              Aplicar
            </button>
          </form>
        </div>
      </aside>

      {/* Results */}
      <div>
        <div className="toolbar">
          <div>
            <h2 style={{ fontSize: "1.6rem" }}>{brand ? `Móviles ${brand}` : "Todos los móviles"}</h2>
            <p className="count">
              {loading ? "Cargando…" : `${data.total} resultados`}
              {data.pages > 1 && !loading && ` · página ${data.page} de ${data.pages}`}
            </p>
          </div>

          <label>
            <span className="eyebrow" style={{ marginRight: 8 }}>
              Ordenar
            </span>
            <select value={sort} onChange={(e) => update({ sort: e.target.value })}>
              {SORTS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        {activeChips.length > 0 && (
          <div className="chips">
            {activeChips.map((chip) => (
              <span key={chip.key} className="chip">
                {chip.label}
                <button onClick={() => update({ [chip.key]: "" })} aria-label={`Quitar ${chip.label}`}>
                  ×
                </button>
              </span>
            ))}
            <button className="linkish" onClick={() => setParams(new URLSearchParams())}>
              Limpiar todo
            </button>
          </div>
        )}

        {error ? (
          <div className="state">
            <h3>No pudimos cargar el catálogo</h3>
            <p>{error}</p>
            <button className="btn" onClick={() => window.location.reload()}>
              Reintentar
            </button>
          </div>
        ) : loading ? (
          <div className="grid">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="card">
                <div className="hero__skeleton" style={{ height: 200 }} />
              </div>
            ))}
          </div>
        ) : data.items.length === 0 ? (
          <div className="state">
            <h3>Ningún móvil coincide con esos filtros</h3>
            <p>Prueba con otra marca o amplía el rango de precio.</p>
            <button className="btn" onClick={() => setParams(new URLSearchParams())}>
              Ver todos los móviles
            </button>
          </div>
        ) : (
          <>
            <div className="grid">
              {data.items.map((product) => (
                <ProductCard key={product.sku} product={product} />
              ))}
            </div>

            {data.pages > 1 && (
              <nav className="pager" aria-label="Paginación">
                <button
                  className="btn btn--ghost"
                  disabled={page <= 1}
                  onClick={() => update({ page: String(page - 1) })}
                >
                  Anterior
                </button>
                <span>
                  {data.page} / {data.pages}
                </span>
                <button
                  className="btn btn--ghost"
                  disabled={page >= data.pages}
                  onClick={() => update({ page: String(page + 1) })}
                >
                  Siguiente
                </button>
              </nav>
            )}
          </>
        )}
      </div>
    </div>
  );
}
